import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import MasterDataPage from "@/components/MasterDataPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { BookMarked, Download, Upload, Library, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const OEM_OPTIONS = ["I-CAR", "Toyota", "Honda", "Ford", "GM", "BMW", "Mercedes-Benz", "Audi", "VW", "Subaru", "Nissan", "Hyundai", "Kia", "Stellantis", "Tesla", "Volvo", "Lexus", "Acura", "Mazda"];

export default function MasterPositionStatements() {
  const [seeding, setSeeding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [operationsLib, setOperationsLib] = useState([]);

  useEffect(() => {
    api.get("/calibration-reports/_operations/library").then((r) => setOperationsLib(r.data)).catch(() => {});
  }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await api.post("/master/position-statements/seed");
      toast.success(`Seeded ${r.data.inserted} OEM/I-CAR position statements`);
      window.location.reload();
    } catch { toast.error("Seed failed"); } finally { setSeeding(false); }
  };

  const opOptions = operationsLib.map((o) => ({ value: o.id, label: o.name }));

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-blue-300 flex items-center gap-2">
            <Library className="w-4 h-4" /> ALLDATA Bridge — Procedure Library
          </div>
          <div className="text-xs text-slate-400 mt-1 max-w-3xl">
            Save OEM repair procedures from ALLDATA, I-CAR RTS, or manufacturer sites once — and they auto-populate on every future Calibration Report that matches the same <span className="text-slate-300 font-medium">Make / Model / Year range / Operation</span>.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="bulk-upload-ps-btn"
            onClick={() => setBulkOpen(true)}
            variant="outline"
            className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10"
          >
            <Upload className="w-4 h-4 mr-2" /> Bulk Upload
          </Button>
          <Button data-testid="seed-ps-btn" disabled={seeding} onClick={seed} className="bg-blue-600 hover:bg-blue-500">
            <Download className="w-4 h-4 mr-2" /> {seeding ? "Seeding..." : "Seed I-CAR / OEM"}
          </Button>
        </div>
      </div>

      <MasterDataPage
        title="OEM Position Statements"
        subtitle="I-CAR and manufacturer official positions — attached automatically to insurance disputes & Calibration Reports"
        entity="position-statements"
        testId="position-statements"
        icon={BookMarked}
        defaultForm={{
          title: "", oem: "", statement_type: "calibration_required",
          summary: "", full_text: "", source_url: "", pdf_url: "",
          tags: [], applies_to_models: [],
          vehicle_make: "", vehicle_model: "",
          year_min: "", year_max: "",
          operation_keys: [], source: "manual",
        }}
        fields={[
          { key: "title", label: "Title", required: true, span: 2 },
          { key: "oem", label: "OEM / Issuer", required: true, type: "select", options: OEM_OPTIONS },
          {
            key: "statement_type", label: "Type", type: "select", options: [
              { value: "calibration_required", label: "Calibration Required" },
              { value: "scan_required", label: "Scan Required" },
              { value: "oem_glass", label: "OEM Glass" },
              { value: "non_oem_parts", label: "Non-OEM Parts" },
              { value: "post_repair", label: "Post-Repair" },
              { value: "general", label: "General" },
            ]
          },
          {
            key: "source", label: "Source", type: "select", options: [
              { value: "manual", label: "Manual entry" },
              { value: "alldata", label: "ALLDATA" },
              { value: "icar", label: "I-CAR RTS" },
              { value: "oem", label: "OEM Direct" },
              { value: "mitchell", label: "Mitchell" },
            ]
          },
          { key: "source_url", label: "Source URL", span: 2, placeholder: "https://my.alldata.com/..." },
          { key: "pdf_url", label: "PDF URL (optional)", span: 2 },
          { key: "summary", label: "Summary", required: true, type: "textarea", span: 2 },
          { key: "full_text", label: "Full Text / Notes", type: "textarea", span: 2 },
          // --- ALLDATA Bridge: vehicle + operation matching ---
          { key: "vehicle_make", label: "Vehicle Make (for auto-match)", placeholder: "e.g. Toyota" },
          { key: "vehicle_model", label: "Vehicle Model", placeholder: "e.g. Camry" },
          { key: "year_min", label: "Year Min", type: "number", placeholder: "e.g. 2020" },
          { key: "year_max", label: "Year Max", type: "number", placeholder: "e.g. 2025" },
          {
            key: "operation_keys", label: "Operation Keys (auto-match on Calibration Report)",
            type: "tags", placeholder: "e.g. front_camera_static, millimeter_wave_radar", span: 2,
          },
          { key: "tags", label: "Tags", type: "tags", placeholder: "comma-separated", span: 2 },
          { key: "effective_date", label: "Effective Date", type: "date" },
        ]}
        columns={[
          { key: "oem", label: "OEM" },
          { key: "title", label: "Title" },
          {
            key: "vehicle_match", label: "Vehicle Match", render: (r) => {
              if (!r.vehicle_make) return <span className="text-slate-600 text-xs italic">any</span>;
              const yr = r.year_min && r.year_max ? ` ${r.year_min}–${r.year_max}` : r.year_min ? ` ${r.year_min}+` : "";
              return <span className="text-xs text-slate-300">{r.vehicle_make} {r.vehicle_model || ""}{yr}</span>;
            }
          },
          {
            key: "operation_keys", label: "Operations", render: (r) => {
              const keys = r.operation_keys || [];
              if (keys.length === 0) return <span className="text-slate-600 text-xs italic">any</span>;
              return (
                <div className="flex flex-wrap gap-1">
                  {keys.slice(0, 2).map((k, i) => (
                    <Badge key={i} className="bg-blue-500/10 border border-blue-500/30 text-blue-300 text-[10px]">{k}</Badge>
                  ))}
                  {keys.length > 2 && <span className="text-[10px] text-slate-500">+{keys.length - 2}</span>}
                </div>
              );
            }
          },
          {
            key: "source", label: "Source", render: (r) => (
              <Badge className="bg-slate-800 border border-slate-700 text-slate-300 text-[10px] uppercase">
                {r.source || "manual"}
              </Badge>
            )
          },
        ]}
      />

      <BulkUploadDialog
        key={`bulk-${bulkOpen ? "o" : "c"}`}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        operationsLib={operationsLib}
        onDone={() => { setBulkOpen(false); window.location.reload(); }}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Bulk Uploader — paste CSV-style rows for fast library seeding
// ──────────────────────────────────────────────────────────────
function BulkUploadDialog({ open, onClose, operationsLib, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState([]);

  const SAMPLE = `# title | oem | make | model | year_min | year_max | operation_keys (semicolon) | source_url | source
Toyota Camry Front Camera Calibration | Toyota | Toyota | Camry | 2018 | 2024 | front_camera_static | https://my.alldata.com/... | alldata
Honda Accord Millimeter Wave Radar | Honda | Honda | Accord | 2019 | 2025 | millimeter_wave_radar | https://my.alldata.com/... | alldata`;

  const parse = (raw) => {
    const items = [];
    raw.split("\n").forEach((line) => {
      const t = line.trim();
      if (!t || t.startsWith("#")) return;
      const parts = t.split("|").map((s) => s.trim());
      if (parts.length < 3) return;
      const [title, oem, make, model, yMin, yMax, opKeys, url, source] = parts;
      items.push({
        title: title || `${make} ${model} procedure`,
        oem: oem || make || "Unknown",
        statement_type: "calibration_required",
        summary: `Bulk-imported procedure for ${make || ""} ${model || ""}`.trim(),
        vehicle_make: make || null,
        vehicle_model: model || null,
        year_min: yMin ? parseInt(yMin, 10) : null,
        year_max: yMax ? parseInt(yMax, 10) : null,
        operation_keys: opKeys ? opKeys.split(";").map((s) => s.trim()).filter(Boolean) : [],
        source_url: url || null,
        source: (source || "manual").toLowerCase(),
        tags: [(source || "manual").toLowerCase(), (make || "").toLowerCase()].filter(Boolean),
      });
    });
    return items;
  };

  const onPreview = () => {
    const items = parse(text);
    setPreview(items);
    if (items.length === 0) toast.error("No valid rows found. Check the format.");
  };

  const submit = async () => {
    const items = parse(text);
    if (items.length === 0) {
      toast.error("No valid rows to upload");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/master/position-statements/bulk", { items });
      toast.success(`Imported ${r.data.inserted} procedures` + (r.data.errors?.length ? ` (${r.data.errors.length} errors)` : ""));
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-4 h-4 text-emerald-400" /> Bulk Upload Procedures</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">
            Paste pipe-separated rows. Fast way to seed your ALLDATA Bridge library from a spreadsheet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-slate-800 bg-slate-900/40 p-2.5 text-[11px] text-slate-400 font-mono">
            <span className="text-slate-500">Format:</span> title | oem | make | model | year_min | year_max | operation_keys (semicolon) | source_url | source
          </div>

          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={SAMPLE}
            className="font-mono text-xs bg-[#030712] border-slate-800 min-h-[200px]"
            data-testid="bulk-upload-textarea"
          />

          <details className="text-xs text-slate-400">
            <summary className="cursor-pointer hover:text-slate-200">Available operation keys (click to expand)</summary>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {operationsLib.map((op) => (
                <button
                  key={op.id}
                  type="button"
                  onClick={() => navigator.clipboard.writeText(op.id)}
                  className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-[10px] text-slate-300 hover:border-blue-500/40 font-mono"
                  title="Click to copy"
                >
                  {op.id} <span className="text-slate-500">({op.name})</span>
                </button>
              ))}
            </div>
          </details>

          {preview.length > 0 && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
              <div className="font-semibold text-emerald-300 mb-2">{preview.length} rows ready to import</div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {preview.slice(0, 10).map((it, i) => (
                  <div key={i} className="text-slate-300 truncate">
                    {it.vehicle_make} {it.vehicle_model} {it.year_min ? `(${it.year_min}-${it.year_max})` : ""} — {it.operation_keys.join(", ")}
                  </div>
                ))}
                {preview.length > 10 && <div className="text-slate-500 text-[10px]">…and {preview.length - 10} more</div>}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" className="border-slate-700" onClick={onClose}>Cancel</Button>
          <Button variant="outline" className="border-slate-700" onClick={onPreview} disabled={!text.trim()} data-testid="bulk-preview-btn">
            <Search className="w-3.5 h-3.5 mr-1.5" /> Preview
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !text.trim()}
            className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="bulk-upload-confirm"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
            Import {preview.length > 0 ? `${preview.length} ` : ""}rows
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
